/**
 * Device Fingerprinting Utility
 * Generates a unique identifier for the browser/device
 * Used for tracking guest users across sessions
 */

/**
 * Generate a device fingerprint based on browser characteristics
 * @returns {string} Base64-encoded device fingerprint
 */
export function generateDeviceFingerprint() {
  const components = [];

  // Browser user agent
  components.push(navigator.userAgent || 'unknown');

  // Language
  components.push(navigator.language || 'unknown');

  // Screen resolution
  components.push(`${window.screen.width}x${window.screen.height}`);

  // Color depth
  components.push(window.screen.colorDepth || 'unknown');

  // Timezone offset
  components.push(new Date().getTimezoneOffset());

  // Platform
  components.push(navigator.platform || 'unknown');

  // CPU cores
  components.push(navigator.hardwareConcurrency || 'unknown');

  // Device memory (if available)
  components.push(navigator.deviceMemory || 'unknown');

  // WebGL vendor and renderer (if available)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown');
        components.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown');
      }
    }
  } catch (e) {
    components.push('webgl-unavailable');
  }

  // Combine all components
  const fingerprint = components.join('|');

  // Encode to Base64 for storage
  return btoa(fingerprint);
}

/**
 * Get or create device fingerprint from localStorage
 * @returns {string} Device fingerprint
 */
export function getDeviceFingerprint() {
  const STORAGE_KEY = 'eladore_device_fingerprint';

  // Try to get existing fingerprint from localStorage
  let fingerprint = localStorage.getItem(STORAGE_KEY);

  if (!fingerprint) {
    // Generate new fingerprint
    fingerprint = generateDeviceFingerprint();

    // Store it for future use
    try {
      localStorage.setItem(STORAGE_KEY, fingerprint);
    } catch (error) {
      console.error('Error saving device fingerprint to localStorage:', error);
    }
  }

  return fingerprint;
}

/**
 * Get device type (mobile, tablet, or desktop)
 * @returns {string} Device type
 */
export function getDeviceType() {
  const ua = navigator.userAgent;

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'web';
}

/**
 * Get browser name
 * @returns {string} Browser name
 */
export function getBrowserName() {
  const ua = navigator.userAgent;
  let browserName = 'Unknown';

  if (ua.indexOf('Firefox') > -1) {
    browserName = 'Firefox';
  } else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) {
    browserName = 'Opera';
  } else if (ua.indexOf('Trident') > -1) {
    browserName = 'Internet Explorer';
  } else if (ua.indexOf('Edge') > -1) {
    browserName = 'Edge';
  } else if (ua.indexOf('Chrome') > -1) {
    browserName = 'Chrome';
  } else if (ua.indexOf('Safari') > -1) {
    browserName = 'Safari';
  }

  return browserName;
}

/**
 * Get operating system
 * @returns {string} OS name
 */
export function getOS() {
  const ua = navigator.userAgent;
  let os = 'Unknown';

  if (ua.indexOf('Win') > -1) {
    os = 'Windows';
  } else if (ua.indexOf('Mac') > -1) {
    os = 'MacOS';
  } else if (ua.indexOf('X11') > -1 || ua.indexOf('Linux') > -1) {
    os = 'Linux';
  } else if (ua.indexOf('Android') > -1) {
    os = 'Android';
  } else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
    os = 'iOS';
  }

  return os;
}

export default {
  getDeviceFingerprint,
  generateDeviceFingerprint,
  getDeviceType,
  getBrowserName,
  getOS
};
